/**
 * `Resolution` — the HITL escalation review queue screen (ADR-0094
 * Slice 3). Renders on the reserved `'resolution'` view id.
 *
 * When a policy rule with `action_on_match: 'escalate'` fires, the
 * transaction is HELD. An Owner/Admin reviews the held order here and
 * approves (→ settles) or rejects (→ cancels). Reviewers can view the
 * queue and reveal buyer detail but cannot resolve; viewers are gated
 * out entirely.
 *
 * Structure mirrors `Mandates`: a serif header + a status pill, a left
 * list section (`EscalationList`) that polls every 10s + supports
 * status filtering + client-side search, and a right detail panel
 * (`EscalationDetail`) with the buyer reveal + Approve/Reject controls.
 *
 * Resolve handling lives here (not in the detail panel) so the
 * optimistic row update + the 409-conflict refetch + the 503-enqueue
 * retry sit next to the list state they mutate:
 *
 *   - success     → optimistic status flip + toast; pending rows leave
 *                   the pending filter on the next poll.
 *   - 409 conflict → coral toast + automatic refetch after 2s.
 *   - 503 enqueue  → amber toast + one automatic retry after 5s.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle2, ShieldX } from 'lucide-react';

import type { AuditRecord, AuditRecordId, UserRole } from '@sill/types';

import { useOptionalApi } from '../../api/api-context.js';
import {
  fetchEscalationPreflightQuote,
  listEscalations,
  resolveEscalation,
  type EscalationItem,
  type EscalationQuote,
  type EscalationStatus,
  type EscalationStatusFilter,
} from '../../api/escalations.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { usePendingEscalationsStore } from '../../app/pending-escalations-context.js';
import { useSites, useToast } from '../../app/providers.js';
import { Drawer, EmptyStateCard, NumLabel } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import { useIsBelowLg } from '../../lib/use-is-mobile.js';
import { EscalationDetail } from './EscalationDetail.js';
import { EscalationList } from './EscalationList.js';

const serifFont = "'Instrument Serif', serif";

/** Poll cadence — matches the Mandates list (10s, visible-tab only). */
const RESOLUTION_POLL_INTERVAL_MS = 10_000;
/** Delay before the automatic refetch after a 409 conflict. */
const CONFLICT_REFETCH_MS = 2_000;
/** Delay before the single automatic retry after a 503 enqueue failure. */
const ENQUEUE_RETRY_MS = 5_000;
/** Bounded auto-retries for a 503 enqueue failure (then manual retry). */
const ENQUEUE_MAX_RETRIES = 1;

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'error'; message: string };

export interface ResolutionProps {
  /** Test-only role override; production reads role from `useAuth()`. */
  role_override?: UserRole;
}

export function Resolution({ role_override }: ResolutionProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const toast = useToast();
  // Active-site selector (global). The queue is scoped to one site —
  // mirrors the Mandates list, which keys its fetch on `activeSite.site_id`
  // and gates on `activeSite`. The site's domain is the human label for
  // every visible row (the server returns only the raw ULID).
  const { activeSite } = useSites();
  // Shared pending-count store — keeps the nav badge in lockstep with
  // this queue: resolving a row decrements it instantly. Destructured to
  // the stable mutators so callbacks don't re-create on every count tick.
  const { markResolved: markPendingResolved, refresh: refreshPendingCount } =
    usePendingEscalationsStore();

  const role: UserRole | null = useMemo(() => {
    if (role_override !== undefined) return role_override;
    if (auth !== null && auth.state.kind === 'authenticated') {
      return auth.state.user.role;
    }
    return null;
  }, [role_override, auth]);

  const can_view = role === 'owner' || role === 'admin' || role === 'reviewer';
  const can_resolve = role === 'owner' || role === 'admin';

  const [items, setItems] = useState<readonly EscalationItem[]>([]);
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [status, setStatus] = useState<EscalationStatusFilter>('pending');
  const [query, setQuery] = useState('');
  // Deep link (email CTA): `?escalation=esc_…` preselects the card once
  // the list loads (the keep-valid-selection effect honors a still-present
  // selected id). Read once at mount; format-pinned.
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const v = new URL(window.location.href).searchParams.get('escalation');
      return v !== null && /^esc_[0-9a-f]{26}$/.test(v) ? v : null;
    } catch {
      return null;
    }
  });
  // One-shot scroll anchor — the escalation the caller deep-linked to
  // (`?escalation=`, captured at mount). The list centers + flashes this
  // row when it first renders. Held in a ref so it stays stable across
  // the 10s poll re-renders and never re-fires as the user clicks around.
  const anchorIdRef = useRef<string | null>(selectedId);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  // ADR-0153 parity with Mandates — on mobile the detail opens as a full-screen
  // Drawer overlay (a deep-selected row shouldn't force a scroll past the whole
  // list to the stacked panel and back). Desktop keeps the sticky side panel.
  // Match Activity (Mandates.tsx): below the `lg` breakpoint the 640 px
  // side panel squeezes the list, so the detail opens as a drawer there
  // too — labels-review/ux.md §10.1.
  const isMobile = useIsBelowLg();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Latest status filter, read by the poll closure without re-subscribing.
  const statusRef = useRef(status);
  statusRef.current = status;

  const refetch = useCallback(
    async (opts?: { silent?: boolean }): Promise<void> => {
      if (http === null || !can_view || activeSite === null) return;
      if (opts?.silent !== true) setLoad((cur) => (cur.kind === 'ready' ? cur : { kind: 'loading' }));
      const result = await listEscalations(http, statusRef.current, activeSite.site_id);
      if (result.kind === 'ok') {
        setItems(result.items);
        setLoad({ kind: 'ready' });
      } else if (result.kind === 'forbidden') {
        setItems([]);
        setLoad({ kind: 'error', message: 'Your role cannot view the escalation queue.' });
      } else {
        setLoad({ kind: 'error', message: result.message });
      }
    },
    [http, can_view, activeSite],
  );

  // Fetch + poll. Re-runs on status change (filter tabs) so a switched
  // tab lands its first fetch immediately. Polling pauses on a hidden
  // tab to conserve API budget, matching the Mandates cadence.
  useEffect(() => {
    if (!can_view || http === null || activeSite === null) return undefined;
    setLoad({ kind: 'loading' });
    void refetch();
    const handle = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void refetch({ silent: true });
    }, RESOLUTION_POLL_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [refetch, can_view, http, status, activeSite]);

  // Keep a valid selection: prefer the still-present selected row,
  // otherwise the first visible row.
  const visibleItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return items;
    return items.filter((it) => {
      const s = it.summary;
      return (
        it.escalation_id.toLowerCase().includes(q)
        || it.mandate_id.toLowerCase().includes(q)
        || it.rule_id.toLowerCase().includes(q)
        || s.merchant.toLowerCase().includes(q)
        || s.action.toLowerCase().includes(q)
        || (s.sku !== undefined && s.sku.toLowerCase().includes(q))
        || (s.line_items !== undefined
          && s.line_items.some((li) => li.sku.toLowerCase().includes(q)))
        || (s.failed_rule_id !== undefined && s.failed_rule_id.toLowerCase().includes(q))
        // Refunds (ADR-0099): the reason code + original mandate are the
        // searchable handles a closed refund intent carries.
        || (s.reason_code !== undefined && s.reason_code.toLowerCase().includes(q))
        || (s.original_mandate_id !== undefined
          && s.original_mandate_id.toLowerCase().includes(q))
      );
    });
  }, [items, query]);

  useEffect(() => {
    setSelectedId((cur) => {
      // While the list is empty (first fetch in flight, or a transient
      // empty filter) PRESERVE the current selection — wiping it here
      // destroyed URL deep links to any non-top card before the data
      // ever arrived (founder report 2026-06-12).
      if (visibleItems.length === 0) return cur;
      if (cur !== null && visibleItems.some((it) => it.escalation_id === cur)) return cur;
      return visibleItems[0]?.escalation_id ?? null;
    });
  }, [visibleItems]);

  const selected = useMemo(
    () => visibleItems.find((it) => it.escalation_id === selectedId) ?? null,
    [visibleItems, selectedId],
  );

  // Pre-flight quote fetch for the SELECTED PENDING escalation
  // (ADR-0094 follow-up). The server runs the rail's calculate (zero
  // mutations) and reports the projected all-in total vs the ceiling —
  // the operator sees a would-fail-on-approve BEFORE deciding. Cached
  // per escalation_id (the quote is stable while the cart is held);
  // rate-limited server-side, so the cache also protects the budget.
  const [quote, setQuote] = useState<EscalationQuote | null>(null);
  const quoteCache = useRef(new Map<string, EscalationQuote>());
  const selectedPendingId =
    selected !== null && selected.status === 'pending' ? selected.escalation_id : undefined;
  useEffect(() => {
    if (http === null || selectedPendingId === undefined) {
      setQuote(null);
      return undefined;
    }
    const cached = quoteCache.current.get(selectedPendingId);
    if (cached !== undefined) {
      setQuote(cached);
      return undefined;
    }
    let cancelled = false;
    setQuote(null);
    void fetchEscalationPreflightQuote(http, selectedPendingId).then((result) => {
      if (cancelled || result.kind !== 'ok') return;
      quoteCache.current.set(selectedPendingId, result.quote);
      setQuote(result.quote);
    });
    return () => {
      cancelled = true;
    };
  }, [http, selectedPendingId]);

  // Settled-evidence fetch for the SELECTED resolved escalation. Once a
  // resolution settled, the list row carries `audit_record_id`; the detail
  // panel then renders the same line-item names + money breakdown
  // (subtotal / tax / shipping / total) the Mandates detail shows.
  // Progressive enhancement: no provider, a fetch failure, or a pending
  // row simply keeps the held-summary view. Cached per record_id so the
  // 10s poll re-renders never refetch a record (audit rows are immutable).
  const api = useOptionalApi();
  const [settledRecord, setSettledRecord] = useState<AuditRecord | null>(null);
  const settledCache = useRef(new Map<string, AuditRecord>());
  const selectedAuditRecordId = selected?.audit_record_id;
  useEffect(() => {
    if (api === null || selectedAuditRecordId === undefined) {
      setSettledRecord(null);
      return undefined;
    }
    const cached = settledCache.current.get(selectedAuditRecordId);
    if (cached !== undefined) {
      setSettledRecord(cached);
      return undefined;
    }
    let cancelled = false;
    setSettledRecord(null);
    void api.mandates
      .get(selectedAuditRecordId as AuditRecordId)
      .then((rec) => {
        if (cancelled || rec === null) return;
        settledCache.current.set(selectedAuditRecordId, rec);
        setSettledRecord(rec);
      })
      .catch(() => {
        // Held summary remains — the settled panel is enhancement only.
      });
    return () => {
      cancelled = true;
    };
  }, [api, selectedAuditRecordId]);

  const onResolve = useCallback(
    async (
      escalation_id: string,
      decision: 'approve' | 'reject',
      attempt = 0,
    ): Promise<void> => {
      if (http === null || !can_resolve) return;
      setResolvingId(escalation_id);
      try {
        const result = await resolveEscalation(http, escalation_id, decision);
        switch (result.kind) {
          case 'ok': {
            // Optimistic status flip — the row leaves the pending filter
            // on the next poll; on the all/decision filters it shows the
            // resolved pill immediately.
            const nextStatus: EscalationStatus = result.status;
            setItems((cur) =>
              cur.map((it) =>
                it.escalation_id === escalation_id
                  ? { ...it, status: nextStatus, resolved_at: new Date().toISOString() }
                  : it,
              ),
            );
            // Optimistically drop the nav badge — this row just left the
            // pending queue (whether approved or rejected). The store's
            // poll reconciles the authoritative count.
            markPendingResolved();
            toast({
              tone: decision === 'approve' ? 'lime' : 'coral',
              title: decision === 'approve' ? 'Escalation approved.' : 'Escalation rejected.',
            });
            break;
          }
          case 'conflict': {
            toast({
              tone: 'coral',
              title: 'This escalation was already resolved by another admin. Refreshing…',
            });
            setTimeout(() => {
              void refetch({ silent: true });
              refreshPendingCount();
            }, CONFLICT_REFETCH_MS);
            break;
          }
          case 'enqueue_failed': {
            // Single bounded auto-retry (the resolve is idempotent — the
            // status flip already committed; a retry just re-pushes). If it
            // still 503s, stop and let the operator retry manually rather
            // than loop forever.
            if (attempt < ENQUEUE_MAX_RETRIES) {
              toast({ tone: 'amber', title: 'Settlement queue unavailable. Retrying in 5s…' });
              setTimeout(() => {
                void onResolve(escalation_id, decision, attempt + 1);
              }, ENQUEUE_RETRY_MS);
            } else {
              toast({
                tone: 'coral',
                title: 'Settlement queue still unavailable.',
                body: 'The decision is recorded — retry resolving in a moment.',
              });
            }
            break;
          }
          case 'forbidden':
            toast({ tone: 'coral', title: 'Your role cannot resolve escalations.' });
            break;
          case 'not_found':
            toast({ tone: 'coral', title: 'Escalation not found — it may have been resolved.' });
            void refetch({ silent: true });
            refreshPendingCount();
            break;
          case 'error':
            toast({ tone: 'coral', title: 'Could not resolve escalation.', body: result.message });
            // Network blip (offline or timeout) — the server may have
            // completed the resolve while the browser-side signal
            // aborted (2026-10-23 founder report on held-order approve).
            // Silently refetch so the list reflects reality, and keep
            // the pending-count badge honest.
            if (result.network !== undefined) {
              void refetch({ silent: true });
              refreshPendingCount();
            }
            break;
        }
      } finally {
        setResolvingId((cur) => (cur === escalation_id ? null : cur));
      }
    },
    [http, can_resolve, toast, refetch, markPendingResolved, refreshPendingCount],
  );

  const pendingCount = useMemo(
    () => items.filter((it) => it.status === 'pending').length,
    [items],
  );

  // Role gate — viewer (or any non-viewing role) sees a single-line
  // hidden notice and the screen never fetches.
  if (!can_view) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <ResolutionHeader C={C} pendingCount={0} />
        <div
          className="p-3 flex items-center gap-2"
          style={{
            background: C.surface2,
            border: `1px dashed ${C.borderSoft}`,
            borderRadius: 2,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
          }}
        >
          <ShieldX size={11} />
          ESCALATIONS · HIDDEN FROM YOUR ROLE
        </div>
      </div>
    );
  }

  // List replacement content — loading skeleton / empty / error.
  const emptyState: React.ReactNode | null = (() => {
    // The queue is per-site scoped (mirrors Mandates). With no active site
    // selected the screen can't fetch — surface a guidance card instead of
    // an indefinite loading state.
    if (activeSite === null) {
      return (
        <EmptyStateCard
          icon={ShieldX}
          label="No site selected"
          hint="Select a site to review its held escalations."
        />
      );
    }
    if (load.kind === 'loading') {
      return (
        <div className="p-5" style={{ minHeight: 180 }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              color: C.muted,
              letterSpacing: 1,
            }}
          >
            Loading escalations…
          </div>
        </div>
      );
    }
    if (load.kind === 'error') {
      return (
        <EmptyStateCard
          icon={AlertCircle}
          label="Failed to load escalations"
          hint={
            <>
              {load.message}
              <button
                type="button"
                onClick={() => void refetch()}
                style={{
                  display: 'block',
                  margin: '10px auto 0',
                  fontFamily: "'Geist', sans-serif",
                  fontSize: 12,
                  color: C.limeFg,
                  fontWeight: 500,
                  textDecoration: 'underline',
                  background: 'transparent',
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </>
          }
        />
      );
    }
    if (visibleItems.length === 0) {
      return (
        <EmptyStateCard
          icon={CheckCircle2}
          label="All caught up"
          hint={
            query.trim().length > 0
              ? 'No escalations match your search.'
              : status === 'pending' || status === 'all'
                ? 'No pending escalations. Approved and rejected orders appear under the other filters.'
                : 'No escalations with this status.'
          }
        />
      );
    }
    return null;
  })();

  // One EscalationDetail, two layouts (mirrors Mandates): a `sticky` side
  // panel on desktop, a `flat` panel inside the mobile Drawer. Only ONE
  // mounts per breakpoint (desktop gates on `!isMobile`, the Drawer on
  // `isMobile`) so there's no double render/fetch.
  const renderDetail = (containerLayout: 'sticky' | 'flat'): JSX.Element | null =>
    selected === null ? null : (
      <EscalationDetail
        item={selected}
        role={role}
        containerLayout={containerLayout}
        busy={resolvingId === selected.escalation_id}
        onResolve={(decision) => void onResolve(selected.escalation_id, decision)}
        {...(activeSite?.domain !== undefined ? { siteDomain: activeSite.domain } : {})}
        {...(settledRecord !== null && selected.audit_record_id !== undefined
          ? { auditRecord: settledRecord }
          : {})}
        {...(quote !== null && selected.status === 'pending' ? { quote } : {})}
      />
    );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <ResolutionHeader C={C} pendingCount={pendingCount} />

      {/* Founder 2026-10-21 — the detail panel used to be `md:w-[420px]`
          while Activity (Mandates.tsx) is `lg:w-[640px]`. On a desktop a
          Held order row ran wider than its own detail; the two views now
          share one split constant. Mobile stays single-column, exactly
          like Activity. */}
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0">
          <EscalationList
            items={visibleItems}
            selectedId={selectedId}
            onSelect={(it) => {
              setSelectedId(it.escalation_id);
              setDrawerOpen(true);
            }}
            status={status}
            onChangeStatus={setStatus}
            query={query}
            onChangeQuery={setQuery}
            emptyState={emptyState}
            scrollAnchorId={anchorIdRef.current}
          />
        </div>
        {/* DESKTOP: sticky side panel (hidden on mobile). Width + sticky
            parameters mirror the Activity split in `Mandates.tsx`. */}
        <div className="hidden lg:block lg:w-[640px] lg:flex-shrink-0 lg:self-start lg:sticky lg:top-24 lg:max-h-[calc(100vh_-_7rem)] lg:overflow-y-auto">
          {!isMobile ? renderDetail('sticky') : null}
        </div>
      </div>

      {/* MOBILE: full-screen slide-over so tapping a row deep in a long list
          opens the detail as an overlay (CLOSE / Esc / backdrop returns to the
          exact scroll position) instead of forcing a scroll to the stacked
          panel and back — parity with the Mandates page. */}
      <AnimatePresence>
        {isMobile && drawerOpen && selected !== null ? (
          <Drawer
            open
            onClose={() => setDrawerOpen(false)}
            title="ESCALATION DETAIL"
            closeLabel="Close escalation detail"
          >
            <div className="p-4">{renderDetail('flat')}</div>
          </Drawer>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function ResolutionHeader({
  C,
  pendingCount,
}: {
  C: ReturnType<typeof useC>;
  pendingCount: number;
}): JSX.Element {
  return (
    <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
      <div>
        <NumLabel n={1}>
          Held orders · {pendingCount} waiting for review
        </NumLabel>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(36px, 6vw, 52px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
          }}
        >
          Held orders, <em>reviewed and resolved</em>.
        </h1>
      </div>
    </div>
  );
}
